import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";

export interface ReleaseNotePreview {
  locale: string;
  text: string;
  isOverride: boolean;
}

interface Props {
  includeReleaseNotes: boolean;
  notes: ReleaseNotePreview[];
}

const InternReleaseNotesPreview = ({ includeReleaseNotes, notes }: Props) => {
  const overrideCount = notes.filter((n) => n.isOverride).length;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: "8px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: "var(--intern-text-tertiary)" }} />
        }
        sx={{
          px: 2,
          minHeight: 52,
          "& .MuiAccordionSummary-content": { my: 1 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ width: "100%", pr: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              What&apos;s New
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.6875rem",
              }}
            >
              {includeReleaseNotes
                ? "Resolved store locales for the next update"
                : "Omitted on first Kinn iOS build"}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${notes.length} locales`}
            sx={{
              height: 22,
              fontSize: "0.625rem",
              fontWeight: 600,
              bgcolor: "var(--intern-bg-secondary)",
              color: "var(--intern-text-secondary)",
            }}
          />
          {overrideCount > 0 ? (
            <Chip
              size="small"
              label={`${overrideCount} override${
                overrideCount === 1 ? "" : "s"
              }`}
              sx={{
                height: 22,
                fontSize: "0.625rem",
                fontWeight: 600,
                bgcolor: "rgba(99, 102, 241, 0.1)",
                color: "#6366f1",
              }}
            />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "1fr 1fr 1fr",
            },
            gap: 1,
          }}
        >
          {notes.map((note) => (
            <Box
              key={note.locale}
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "var(--intern-bg-secondary)",
                border: "1px solid var(--intern-border-default)",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={0.5}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "var(--intern-text-primary)",
                    fontSize: "0.6875rem",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {note.locale}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    color: note.isOverride
                      ? "#6366f1"
                      : "var(--intern-text-tertiary)",
                  }}
                >
                  {note.isOverride ? "Override" : "Default"}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  color: "var(--intern-text-secondary)",
                  fontSize: "0.75rem",
                  lineHeight: 1.4,
                }}
              >
                {note.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default InternReleaseNotesPreview;
